import environment from '../../../environment'
import { getAccessToken, getAuthHeaders } from '../auth/auth'
import { AuthError, ForbiddenError, NotFoundError, NetworkError } from './customError'

interface FetchOptions extends RequestInit {
  searchParams?: UrlParams
}
// TODO: Refactor this type to only allow 'URLSearchParams'.
export type UrlParams = URLSearchParams | { [key: string]: string }

export const handleErrors = (response: Response) => {
  if (response.status === 401) {
    throw new AuthError()
  }

  if (response.status === 403) {
    throw new ForbiddenError()
  }

  if (response.status === 404) {
    throw new NotFoundError(response.statusText)
  }

  if (!response.ok) {
    throw new NetworkError(response.status, response.statusText)
  }

  return response
}

export const createRequestHeaders = (
  headers: Headers | undefined = undefined,
  includeApiKey = true,
) => {
  const requestHeaders = headers ?? new Headers()
  const token = getAccessToken()

  if (includeApiKey) {
    requestHeaders.append('X-Api-Key', environment.DSO_API_KEY)
  }

  if (token.length > 0) {
    requestHeaders.append('Authorization', `Bearer ${token}`)
  }

  return requestHeaders
}

interface FetchOpts {
  signal?: AbortSignal
  headers?: Headers
}

export const fetchRaw = <T = any>(
  url: string,
  params?: UrlParams,
  { signal, headers }: FetchOpts = {},
): Promise<T> => {
  const searchParams = (
    params instanceof URLSearchParams ? params : new URLSearchParams(params)
  ).toString()
  const fullUrl = searchParams ? `${url}?${searchParams.toString()}` : url

  return fetch(fullUrl, { headers, signal })
    .then((response) => handleErrors(response))
    .then((response) => response.json())
}

export const fetchWithAPIKey = <T = any>(
  url: string,
  params?: UrlParams,
  { signal, headers }: FetchOpts = {},
): Promise<T> => {
  const headersWithKey = headers ?? new Headers()
  headersWithKey.append('X-Api-Key', environment.DSO_API_KEY)

  return fetchRaw(url, params, { headers: headersWithKey, signal })
}

export const fetchWithToken = <T = any>(
  url: string,
  params?: UrlParams,
  { signal, headers, includeApiKey }: FetchOpts & { includeApiKey?: boolean } = {},
): Promise<T> => {
  const headersWithKeys = createRequestHeaders(headers, includeApiKey)

  return fetchRaw(url, params, { headers: headersWithKeys, signal })
}

export const createUrlWithToken = (url: string, token: string) => {
  const parsedUrl = new URL(url)

  if (token.length > 0) {
    parsedUrl.searchParams.set('access_token', token)
  }

  return parsedUrl.toString()
}

export const fetchProxy = <T = any>(
  url: string,
  init: FetchOptions = {},
  includeApiKey = true,
): Promise<T> => {
  const { headers, searchParams = {}, ...otherOptions } = init
  const requestHeaders = new Headers(headers)

  if (includeApiKey) {
    requestHeaders.append('X-Api-Key', environment.DSO_API_KEY)
  }

  const authHeaders = Object.entries(getAuthHeaders())

  authHeaders.forEach(([name, value]) => {
    if (value) requestHeaders.append(name, value)
  })

  const options: RequestInit = {
    ...otherOptions,
    headers: requestHeaders,
  }

  const fullUrl = new URL(url)
  const params = new URLSearchParams(searchParams)
  const paramsInUrl = fullUrl.searchParams

  params.forEach((value, key) => {
    paramsInUrl.set(key, value)
  })

  fullUrl.search = paramsInUrl.toString()

  return fetch(fullUrl.toString(), options)
    .then((response) => handleErrors(response))
    .then((response) => response.json()) as Promise<T>
}
